import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Mountain, Users, Calendar, FolderOpen, 
  BookOpen, Images, Bed, MessageSquare, Heart, Settings,
  LogOut, Menu, X, Search, Save, Plus, Globe, Trash2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage, LANGUAGES } from '../../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIDEBAR_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: Mountain, label: 'Falesie', path: '/admin/crags' },
  { icon: Users, label: 'Team', path: '/admin/team' },
  { icon: Calendar, label: 'Eventi', path: '/admin/events' },
  { icon: FolderOpen, label: 'Progetti', path: '/admin/projects' },
  { icon: BookOpen, label: 'Blog', path: '/admin/blog' },
  { icon: Images, label: 'Gallery', path: '/admin/gallery' },
  { icon: Bed, label: 'Alloggi', path: '/admin/accommodations' },
  { icon: MessageSquare, label: 'Messaggi', path: '/admin/messages' },
  { icon: Heart, label: 'Donazioni', path: '/admin/donations' },
  { icon: Search, label: 'SEO', path: '/admin/seo' },
  { icon: Images, label: 'Uploads', path: '/admin/uploads' },
  { icon: Settings, label: 'Impostazioni', path: '/admin/settings' },
];

const DEFAULT_PAGES = [
  { key: 'home', label: 'Home' },
  { key: 'about', label: 'Chi Siamo' },
  { key: 'crags', label: 'Falesie' },
  { key: 'events', label: 'Eventi' },
  { key: 'projects', label: 'Progetti' },
  { key: 'blog', label: 'Blog' },
  { key: 'gallery', label: 'Gallery' },
  { key: 'contact', label: 'Contatti' },
  { key: 'donations', label: 'Donazioni' },
  { key: 'accommodation', label: 'Alloggi' },
  { key: 'how-to-arrive', label: 'Come Arrivare' },
];

export function AdminSEO() {
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [seoData, setSeoData] = useState([]);
  const [selectedPage, setSelectedPage] = useState('home');
  const [currentSEO, setCurrentSEO] = useState({
    page_key: 'home',
    meta_title: { it: '', en: '', fr: '', de: '', es: '' },
    meta_description: { it: '', en: '', fr: '', de: '', es: '' },
    meta_keywords: { it: '', en: '', fr: '', de: '', es: '' },
    og_image: '',
    canonical_url: '',
    robots: 'index, follow'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeLang, setActiveLang] = useState('it');

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }
    fetchAllSEO();
  }, [isAuthenticated, authLoading, navigate]);

  const fetchAllSEO = async () => {
    try {
      const res = await axios.get(`${API}/seo`);
      setSeoData(res.data);
    } catch (error) {
      console.error('Error fetching SEO:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPageSEO = async (pageKey) => {
    try {
      const res = await axios.get(`${API}/seo/${pageKey}`);
      setCurrentSEO({
        page_key: pageKey,
        meta_title: res.data.meta_title || { it: '', en: '', fr: '', de: '', es: '' },
        meta_description: res.data.meta_description || { it: '', en: '', fr: '', de: '', es: '' },
        meta_keywords: res.data.meta_keywords || { it: '', en: '', fr: '', de: '', es: '' },
        og_image: res.data.og_image || '',
        canonical_url: res.data.canonical_url || '',
        robots: res.data.robots || 'index, follow'
      });
    } catch (error) {
      // Reset to defaults for new page
      setCurrentSEO({
        page_key: pageKey,
        meta_title: { it: '', en: '', fr: '', de: '', es: '' },
        meta_description: { it: '', en: '', fr: '', de: '', es: '' },
        meta_keywords: { it: '', en: '', fr: '', de: '', es: '' },
        og_image: '',
        canonical_url: '',
        robots: 'index, follow'
      });
    }
  };

  useEffect(() => {
    if (selectedPage) {
      fetchPageSEO(selectedPage);
    }
  }, [selectedPage]);

  const handleInputChange = (field, lang, value) => {
    setCurrentSEO(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [lang]: value
      }
    }));
  };

  const handleSimpleChange = (field, value) => {
    setCurrentSEO(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/seo`, currentSEO);
      toast.success('SEO salvato con successo');
      fetchAllSEO();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-seo-page">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0A0A0A] transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-800">
          <Link to="/" className="text-white font-heading text-lg uppercase">CMS Admin</Link>
          <button className="lg:hidden text-white" onClick={() => setSidebarOpen(false)}>
            <X className="w-6 h-6" />
          </button>
        </div>
        <nav className="p-4 space-y-1 max-h-[calc(100vh-8rem)] overflow-y-auto">
          {SIDEBAR_ITEMS.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                item.path === '/admin/seo' ? 'bg-[#FF6D00] text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-sm">{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800">
          <button onClick={logout} className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white w-full">
            <LogOut className="w-5 h-5" />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        <header className="bg-white border-b h-16 flex items-center justify-between px-6">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="font-heading text-xl uppercase">Gestione SEO</h1>
          <Link to="/" className="text-sm text-[#FF6D00] hover:underline">Vedi sito</Link>
        </header>

        <main className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Page Selector */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg border p-4">
                <h2 className="font-heading uppercase mb-4">Pagine</h2>
                <div className="space-y-1">
                  {DEFAULT_PAGES.map(page => (
                    <button
                      key={page.key}
                      onClick={() => setSelectedPage(page.key)}
                      className={`w-full text-left px-4 py-2 rounded transition-colors ${
                        selectedPage === page.key ? 'bg-[#FF6D00] text-white' : 'hover:bg-gray-100'
                      }`}
                    >
                      {page.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* SEO Editor */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-lg border p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-heading text-xl uppercase">
                    SEO: {DEFAULT_PAGES.find(p => p.key === selectedPage)?.label}
                  </h2>
                  <Button onClick={handleSave} disabled={saving} className="btn-primary">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Salvataggio...' : 'Salva'}
                  </Button>
                </div>

                {/* Language Tabs */}
                <Tabs value={activeLang} onValueChange={setActiveLang} className="mb-6">
                  <TabsList>
                    {Object.values(LANGUAGES).map(lang => (
                      <TabsTrigger key={lang.code} value={lang.code}>
                        {lang.flag} {lang.code.toUpperCase()}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {Object.values(LANGUAGES).map(lang => (
                    <TabsContent key={lang.code} value={lang.code} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Meta Title ({lang.name})
                        </label>
                        <Input
                          value={currentSEO.meta_title?.[lang.code] || ''}
                          onChange={(e) => handleInputChange('meta_title', lang.code, e.target.value)}
                          placeholder={`Titolo pagina in ${lang.name}`}
                          data-testid={`seo-title-${lang.code}`}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {(currentSEO.meta_title?.[lang.code] || '').length}/60 caratteri
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Meta Description ({lang.name})
                        </label>
                        <Textarea
                          value={currentSEO.meta_description?.[lang.code] || ''}
                          onChange={(e) => handleInputChange('meta_description', lang.code, e.target.value)}
                          placeholder={`Descrizione pagina in ${lang.name}`}
                          rows={3}
                          data-testid={`seo-desc-${lang.code}`}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {(currentSEO.meta_description?.[lang.code] || '').length}/160 caratteri
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Meta Keywords ({lang.name})
                        </label>
                        <Input
                          value={currentSEO.meta_keywords?.[lang.code] || ''}
                          onChange={(e) => handleInputChange('meta_keywords', lang.code, e.target.value)}
                          placeholder="keyword1, keyword2, keyword3"
                          data-testid={`seo-keywords-${lang.code}`}
                        />
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>

                {/* Common fields */}
                <div className="space-y-4 pt-4 border-t">
                  <div>
                    <label className="block text-sm font-medium mb-2">OG Image URL</label>
                    <Input
                      value={currentSEO.og_image || ''}
                      onChange={(e) => handleSimpleChange('og_image', e.target.value)}
                      placeholder="https://..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Canonical URL</label>
                    <Input
                      value={currentSEO.canonical_url || ''}
                      onChange={(e) => handleSimpleChange('canonical_url', e.target.value)}
                      placeholder="https://caltavuturoclimbing.it/..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Robots</label>
                    <Select
                      value={currentSEO.robots || 'index, follow'}
                      onValueChange={(value) => handleSimpleChange('robots', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="index, follow">index, follow</SelectItem>
                        <SelectItem value="index, nofollow">index, nofollow</SelectItem>
                        <SelectItem value="noindex, follow">noindex, follow</SelectItem>
                        <SelectItem value="noindex, nofollow">noindex, nofollow</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Preview */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-heading uppercase text-sm mb-3">Anteprima Google</h3>
                  <div className="bg-white p-4 rounded border">
                    <p className="text-blue-700 text-lg truncate">
                      {currentSEO.meta_title?.[activeLang] || 'Titolo pagina'}
                    </p>
                    <p className="text-green-700 text-sm truncate">
                      https://caltavuturoclimbing.it/{selectedPage === 'home' ? '' : selectedPage}
                    </p>
                    <p className="text-gray-600 text-sm line-clamp-2">
                      {currentSEO.meta_description?.[activeLang] || 'Descrizione della pagina...'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
